/**
 * Utility function to convert full state name to state code
 * Maps state names from lead data to state codes for API filtering
 */

// Country name to code (API expects code e.g. "US", not "United States")
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  'United States': 'US',
  'United States of America': 'US',
  USA: 'US',
  US: 'US',
  Canada: 'CA',
  CA: 'CA',
  Mexico: 'MX',
  MX: 'MX'
};

/**
 * Converts country name to API country code (e.g. "United States" → "US").
 * Used for setback details and other APIs that expect country code.
 */
export const getCountryCodeFromName = (countryName: string): string | null => {
  if (!countryName) return null;
  const trimmed = countryName.trim();
  const code = COUNTRY_NAME_TO_CODE[trimmed];
  return code ?? (trimmed.length === 2 ? trimmed : null);
};

/**
 * Extracts country code from lead data (address or property country).
 */
export const getCountryCodeFromLead = (leadData: any): string | null => {
  const name =
    leadData?.address?.country ?? leadData?.property?.country ?? null;
  if (!name) return null;
  return getCountryCodeFromName(name);
};

// State name to code mapping (based on countries.py)
const STATE_NAME_TO_CODE: Record<string, string> = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  'District of Columbia': 'DC',
  Delaware: 'DE',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
  // Canadian provinces
  Alberta: 'AB',
  'British Columbia': 'BC',
  Manitoba: 'MB',
  Saskatchewan: 'SK',
  Ontario: 'ON',
  Québec: 'QC',
  'New Brunswick': 'NB',
  'Nova Scotia': 'NS',
  'Newfoundland and Labrador': 'NL',
  'Prince Edward Island': 'PE',
  Yukon: 'YT',
  'Northwest Territories': 'NT',
  Nunavut: 'NU'
};

/** Set of known state/province codes for pass-through when lead already has code. */
const STATE_CODES = new Set(Object.values(STATE_NAME_TO_CODE));

/**
 * Converts a full state/province name or code to its corresponding code
 * @param stateName - Full state name (e.g., "California") or already a code (e.g., "CA")
 * @returns State code (e.g., "CA", "NY") or null if not found
 */
export const getStateCodeFromName = (stateName: string): string | null => {
  if (!stateName) return null;

  const trimmed = stateName.trim();
  // Lead may already have state as code (e.g. "CA"); pass through if it's a known code
  if (trimmed.length === 2 && STATE_CODES.has(trimmed)) return trimmed;

  const stateCode = STATE_NAME_TO_CODE[trimmed];
  return stateCode || null;
};

/**
 * Converts a state code to its full name
 * @param stateCode - State code (e.g., "CA", "NY")
 * @returns Full state name (e.g., "California", "New York") or null if not found
 */
export const getStateNameFromCode = (stateCode: string): string | null => {
  if (!stateCode) return null;

  // Create reverse mapping
  const codeToName = Object.entries(STATE_NAME_TO_CODE).reduce(
    (acc, [name, code]) => {
      acc[code] = name;
      return acc;
    },
    {} as Record<string, string>
  );

  return codeToName[stateCode] || null;
};

/**
 * Extracts state code from lead data.
 * Supports both property.state (e.g. REST/CRM) and address.state (e.g. lead payload).
 * @param leadData - Lead data object containing property and/or address
 * @returns State code or null if not found
 */
export const getStateCodeFromLead = (leadData: any): string | null => {
  const stateName =
    leadData?.property?.state ?? leadData?.address?.state ?? null;
  if (!stateName) return null;

  return getStateCodeFromName(stateName);
};

/**
 * Logs state mapping for debugging
 * @param stateName - Full state name
 * @param stateCode - State code
 */
export const logStateMapping = (
  stateName: string,
  stateCode: string | null
) => {
  console.log('🗺️ State Mapping:', {
    stateName,
    stateCode,
    found: !!stateCode
  });
};
